/**
 * Analytics Service - Central hub for all tracking
 *
 * Integrates:
 * - Google Analytics 4 (GA4) - Usage analytics
 * - Sentry - Error tracking and monitoring
 * - Microsoft Clarity - Heatmaps and session replay
 */

import * as Sentry from '@sentry/react';

// =============================================================================
// CONFIGURATION
// =============================================================================

const GA_MEASUREMENT_ID = 'G-X2Q5FK14B6';

// These will be set via environment variables
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN || '';
const CLARITY_PROJECT_ID = import.meta.env.VITE_CLARITY_PROJECT_ID || '';

const isDevelopment = import.meta.env.DEV;

// =============================================================================
// TYPE DEFINITIONS
// =============================================================================

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
    clarity: (...args: any[]) => void;
  }
}

export interface UserProperties {
  userId?: string;
  email?: string;
  name?: string;
  company?: string;
  role?: 'admin' | 'client' | 'consultant';
  plan?: string;
}

export interface TrackEventParams {
  category: string;
  action: string;
  label?: string;
  value?: number;
  customParams?: Record<string, any>;
}

// =============================================================================
// INITIALIZATION
// =============================================================================

/**
 * Initialize all analytics services
 */
export function initializeAnalytics(): void {
  initializeSentry();
  initializeClarity();

  if (isDevelopment) {
    console.log('[Analytics] Initialized in development mode');
  }
}

/**
 * Initialize Sentry for error tracking
 */
function initializeSentry(): void {
  if (!SENTRY_DSN) {
    if (isDevelopment) {
      console.warn('[Sentry] DSN not configured - error tracking disabled');
    }
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.VITE_ENVIRONMENT || 'development',

    // Performance monitoring
    tracesSampleRate: isDevelopment ? 1.0 : 0.2, // 20% of transactions in production

    // Session replay for errors
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors

    // Filter out noisy errors
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
      /^Network Error$/,
      /^Loading chunk \d+ failed/,
    ],

    // Don't send errors in development unless explicitly enabled
    enabled: !isDevelopment || import.meta.env.VITE_SENTRY_DEBUG === 'true',

    beforeSend(event) {
      // Don't send events if user hasn't consented (LGPD compliance)
      if (localStorage.getItem('analytics_consent') === 'false') {
        return null;
      }
      return event;
    },
  });

  if (isDevelopment) {
    console.log('[Sentry] Initialized');
  }
}

/**
 * Initialize Microsoft Clarity for heatmaps and session replay
 */
function initializeClarity(): void {
  if (!CLARITY_PROJECT_ID) {
    if (isDevelopment) {
      console.warn('[Clarity] Project ID not configured - heatmaps disabled');
    }
    return;
  }

  // Clarity is loaded via script tag in index.html
  // This function just validates it's available
  if (typeof window.clarity === 'function') {
    if (isDevelopment) {
      console.log('[Clarity] Already initialized');
    }
  }
}

// =============================================================================
// USER IDENTIFICATION
// =============================================================================

/**
 * Identify user across all analytics platforms
 * Call this after successful login
 */
export function identifyUser(properties: UserProperties): void {
  const { userId, email, name, company, role } = properties;

  // GA4 - Set user ID and properties
  if (typeof window.gtag === 'function' && userId) {
    window.gtag('config', GA_MEASUREMENT_ID, {
      user_id: userId,
    });

    window.gtag('set', 'user_properties', {
      user_role: role,
      user_company: company,
      user_plan: properties.plan,
    });
  }

  // Sentry - Set user context
  if (SENTRY_DSN) {
    Sentry.setUser({
      id: userId,
      email: email,
      username: name,
    });

    Sentry.setTag('user_role', role || 'unknown');
    Sentry.setTag('user_company', company || 'unknown');
  }

  // Clarity - Identify user
  if (typeof window.clarity === 'function' && userId) {
    window.clarity('identify', userId, undefined, undefined, name);

    if (company) {
      window.clarity('set', 'company', company);
    }
    if (role) {
      window.clarity('set', 'role', role);
    }
  }

  if (isDevelopment) {
    console.log('[Analytics] User identified:', { userId, role, company });
  }
}

/**
 * Clear user identification (call on logout)
 */
export function clearUser(): void {
  // Sentry
  Sentry.setUser(null);

  // GA4 - Clear user ID
  if (typeof window.gtag === 'function') {
    window.gtag('config', GA_MEASUREMENT_ID, {
      user_id: undefined,
    });
  }

  if (isDevelopment) {
    console.log('[Analytics] User cleared');
  }
}

// =============================================================================
// EVENT TRACKING
// =============================================================================

/**
 * Track a custom event
 */
export function trackEvent(params: TrackEventParams): void {
  const { category, action, label, value, customParams } = params;

  // Check consent
  if (localStorage.getItem('analytics_consent') === 'false') {
    return;
  }

  // GA4 Event
  if (typeof window.gtag === 'function') {
    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value,
      ...customParams,
    });
  }

  // Clarity custom event tag
  if (typeof window.clarity === 'function') {
    window.clarity('set', `${category}_${action}`, label || 'true');
  }

  if (isDevelopment) {
    console.log('[Analytics] Event:', { category, action, label, value });
  }
}

/**
 * Track page view (for SPA navigation)
 */
export function trackPageView(path: string, title?: string): void {
  if (localStorage.getItem('analytics_consent') === 'false') {
    return;
  }

  if (typeof window.gtag === 'function') {
    window.gtag('event', 'page_view', {
      page_path: path,
      page_title: title || document.title,
      page_location: window.location.href,
    });
  }

  if (isDevelopment) {
    console.log('[Analytics] Page view:', path);
  }
}

// =============================================================================
// PRE-BUILT EVENT HELPERS
// =============================================================================

export const AnalyticsEvents = {
  // Authentication
  login: (method: string = 'azure_ad') => trackEvent({
    category: 'authentication',
    action: 'login',
    label: method,
  }),

  logout: () => trackEvent({
    category: 'authentication',
    action: 'logout',
  }),

  // Navigation
  menuClick: (menuItem: string) => trackEvent({
    category: 'navigation',
    action: 'menu_click',
    label: menuItem,
  }),

  // Documents
  documentView: (docId: string, docType: string) => trackEvent({
    category: 'documents',
    action: 'view',
    label: docType,
    customParams: { document_id: docId },
  }),

  documentDownload: (docId: string, docType: string) => trackEvent({
    category: 'documents',
    action: 'download',
    label: docType,
    customParams: { document_id: docId },
  }),

  documentUpload: (docType: string) => trackEvent({
    category: 'documents',
    action: 'upload',
    label: docType,
  }),

  // Contracts
  contractView: (contractId: string) => trackEvent({
    category: 'contracts',
    action: 'view',
    customParams: { contract_id: contractId },
  }),

  contractSign: (contractId: string) => trackEvent({
    category: 'contracts',
    action: 'sign',
    customParams: { contract_id: contractId },
  }),

  // Projects
  projectView: (projectId: string, projectName: string) => trackEvent({
    category: 'projects',
    action: 'view',
    label: projectName,
    customParams: { project_id: projectId },
  }),

  projectCreate: (projectName: string) => trackEvent({
    category: 'projects',
    action: 'create',
    label: projectName,
  }),

  // Forms
  formStart: (formId: string, formName: string) => trackEvent({
    category: 'forms',
    action: 'start',
    label: formName,
    customParams: { form_id: formId },
  }),

  formSubmit: (formId: string, formName: string) => trackEvent({
    category: 'forms',
    action: 'submit',
    label: formName,
    customParams: { form_id: formId },
  }),

  formAbandon: (formId: string, formName: string, step: number) => trackEvent({
    category: 'forms',
    action: 'abandon',
    label: formName,
    customParams: { form_id: formId, abandoned_at_step: step },
  }),

  // Search
  search: (query: string, resultsCount: number) => trackEvent({
    category: 'search',
    action: 'search',
    label: query,
    value: resultsCount,
  }),

  // Errors (user-facing)
  errorDisplayed: (errorCode: string, errorMessage: string) => trackEvent({
    category: 'errors',
    action: 'displayed',
    label: errorCode,
    customParams: { error_message: errorMessage },
  }),

  // Feature usage
  featureUsed: (featureName: string, details?: string) => trackEvent({
    category: 'feature_usage',
    action: featureName,
    label: details,
  }),

  // Admin actions
  adminAction: (action: string, target: string) => trackEvent({
    category: 'admin',
    action: action,
    label: target,
  }),

  // Public site
  ctaClick: (ctaName: string, location: string) => trackEvent({
    category: 'cta',
    action: 'click',
    label: ctaName,
    customParams: { cta_location: location },
  }),

  contactFormSubmit: (formType: string) => trackEvent({
    category: 'lead_generation',
    action: 'contact_form_submit',
    label: formType,
  }),

  solutionView: (solutionId: string, solutionName: string) => trackEvent({
    category: 'solutions',
    action: 'view',
    label: solutionName,
    customParams: { solution_id: solutionId },
  }),
};

// =============================================================================
// ERROR TRACKING
// =============================================================================

/**
 * Capture an error manually
 */
export function captureError(error: Error, context?: Record<string, any>): void {
  if (SENTRY_DSN) {
    Sentry.captureException(error, {
      extra: context,
    });
  }

  // Also track in GA4 as event
  trackEvent({
    category: 'errors',
    action: 'javascript_error',
    label: error.message,
    customParams: {
      error_name: error.name,
      error_stack: error.stack?.substring(0, 500),
      ...context,
    },
  });

  if (isDevelopment) {
    console.error('[Analytics] Error captured:', error, context);
  }
}

/**
 * Add breadcrumb for debugging
 */
export function addBreadcrumb(
  message: string,
  category: string = 'user_action',
  data?: Record<string, any>
): void {
  if (SENTRY_DSN) {
    Sentry.addBreadcrumb({
      message,
      category,
      data,
      level: 'info',
    });
  }
}

// =============================================================================
// PERFORMANCE TRACKING
// =============================================================================

/**
 * Send Web Vitals to GA4
 */
export function sendWebVitalsToGA4(metric: {
  name: string;
  value: number;
  id: string;
}): void {
  if (typeof window.gtag !== 'function') return;

  window.gtag('event', metric.name, {
    event_category: 'Web Vitals',
    event_label: metric.id,
    value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
    non_interaction: true,
  });

  if (isDevelopment) {
    console.log('[Analytics] Web Vital:', metric.name, metric.value);
  }
}

// =============================================================================
// CONSENT MANAGEMENT (LGPD)
// =============================================================================

/**
 * Set user consent for analytics
 */
export function setAnalyticsConsent(granted: boolean): void {
  localStorage.setItem('analytics_consent', granted ? 'true' : 'false');

  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      analytics_storage: granted ? 'granted' : 'denied',
    });
  }

  if (isDevelopment) {
    console.log('[Analytics] Consent updated:', granted);
  }
}

/**
 * Check if user has given consent
 */
export function hasAnalyticsConsent(): boolean {
  const consent = localStorage.getItem('analytics_consent');
  // Default to true if not explicitly denied (you may want to change this for LGPD)
  return consent !== 'false';
}

// =============================================================================
// EXPORTS
// =============================================================================

export { Sentry };
